import { useEffect, useRef, useState } from 'react';

/**
 * Reveals an element the first time it enters the viewport —
 * BUILD-PLAN §47 (subtle animation), §40/§47 (reduced motion).
 *
 * Uses IntersectionObserver rather than scroll listeners, so it costs nothing
 * on the main thread while scrolling.
 *
 * Two behaviours matter for correctness rather than polish:
 *
 *  1. It unobserves after the first reveal. Content that re-hides and
 *     re-animates as the user scrolls back up is distracting, and §47 warns
 *     against excessive animation.
 *  2. It returns `true` immediately when the user prefers reduced motion, or
 *     when IntersectionObserver is unavailable. The element must never be
 *     left invisible because an enhancement did not run — that would hide
 *     content rather than merely skip an animation.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: {
  /** Fraction of the element that must be visible. */
  threshold?: number;
  /** Reveal slightly before the element reaches the viewport edge. */
  rootMargin?: string;
}) {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);

  const threshold = options?.threshold ?? 0.15;
  const rootMargin = options?.rootMargin ?? '0px 0px -60px 0px';

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);

      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);

    /*
     * Failsafe. The reveal works by holding content at opacity 0 until the
     * observer fires — so if it never fires, the content is not merely
     * un-animated, it is *invisible*. That has been observed with prerender
     * and screenshot engines, and would equally affect any browser where the
     * callback is delayed or suppressed.
     *
     * Content availability must never depend on an animation, so this reveals
     * unconditionally after a short grace period. In normal use the observer
     * has long since fired and disconnected.
     */
    const failsafe = window.setTimeout(() => {
      setRevealed(true);
      observer.disconnect();
    }, 1500);

    return () => {
      window.clearTimeout(failsafe);
      observer.disconnect();
    };
  }, [threshold, rootMargin]);

  return { ref, revealed };
}

/**
 * Tailwind classes for the standard reveal: a short rise and fade.
 *
 * 700ms with a gentle ease — long enough to feel considered, short enough
 * not to make the page feel slow.
 */
export function revealClasses(revealed: boolean): string {
  return [
    'transition-all duration-700 ease-out motion-reduce:transition-none',
    revealed ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
  ].join(' ');
}
